import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { catchError, Observable, of, shareReplay, Subject } from 'rxjs';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { StaticLookup } from 'src/app/features/company-portal/modules/suppliers/models/SupplierModel';
import { environment } from '../../environments/environment';
import {
  ConfirmEmail,
  LoginModel,
  LoginPayload,
  ProductImageResponse,
  RumSettings,
  Signout,
  UserPermissions,
} from '../models/shared.models';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private companyApiUrl: string = environment.companyApiUrl;
  constructor(
    private http: HttpClient,
    private toastr: CustomToastService,
    private cookieService: CookieService,
  ) {}
  private companyAppsCache = new Map<number, Observable<any>>();
  private appsCache = new Map<string, Observable<any>>();
  private rumSettings$: Observable<RumSettings> | null = null;
  login(userDetails: LoginPayload) {
    const loginPayload = {
      userName: userDetails.userName,
      password: userDetails.password,
    };

    const url = `${this.companyApiUrl}/Auth/SignIn`;
    return this.http.post(url, loginPayload);
  }

  logout(userDetails: Signout) {
    const url = `${this.companyApiUrl}/Auth/SignOut`;
    return this.http.post(url, userDetails);
  }

  public performFullSessionCleanup(): void {
    const domain = environment.domainName;
    localStorage.clear();
    sessionStorage.clear();
    this.clearAppsCache();
    this.clearCompanyAppsCache();
    this.cookieService.deleteAll('/', `.${domain}`);
    this.cookieService.deleteAll('/', `${domain}`);
    this.cookieService.deleteAll('/');
    this.cookieService.deleteAll('/shared');
  }

  setPassword(userDetails: LoginModel) {  
    const loginPayload = {
      username: userDetails.userName,
      password: userDetails.password,
      token: userDetails.token,
    };

    const url = `${this.companyApiUrl}/Users/Self/SetPassword`;
    return this.http.post(url, loginPayload);
  }

  resetPasswordInitiate(userDetails: LoginModel) {
    const loginPayload = {
      userName: userDetails.userName,
    };

    const url = `${this.companyApiUrl}/Users/Self/ResetPassword/Initiate`;
    return this.http.post(url, loginPayload);
  }

  resetPassword(userDetails: LoginModel) {
    const loginPayload = {
      username: userDetails.userName,
      password: userDetails.password,
      token: userDetails.token,
    };

    const url = `${this.companyApiUrl}/Users/Self/ResetPassword`;
    return this.http.post(url, loginPayload);
  }

  getApps(): Observable<any> {
    const cacheKey = 'all'; // use a constant key since this is not company-specific

    if (this.appsCache.has(cacheKey)) {
      return this.appsCache.get(cacheKey)!;
    }

    const url = `${this.companyApiUrl}/Apps`;
    const request$ = this.http.get(url).pipe(
      shareReplay(1), // cache the response
    );

    this.appsCache.set(cacheKey, request$);
    return request$;
  }

  // Optional: clear entire or specific cache
  clearAppsCache(): void {
    this.appsCache.clear();
  }

  confirmImageUpload(blobFileId: number) {
    const url = `${this.companyApiUrl}/BlobFiles/${blobFileId}/Confirm`;
    return this.http.post(url, {});
  }

  uploadImage(companyID: number, file: File | undefined) {
    const formData = new FormData();
    formData.append('File', file || '');
    const url = `${
      this.companyApiUrl
    }/BlobFiles/Upload?path=companies/${companyID}/images&companyId=${companyID}&app=${5}`;
    return this.http.post<ProductImageResponse>(url, formData);
  }

  sendEmailOtp(emailID: string, firstName: string = '', lastName: string = '') {
    const email = {
      firstName: firstName,
      lastName: lastName,
      email: emailID,
    };
    const url = `${this.companyApiUrl}/Users/ConfirmEmail/Initiate`;
    return this.http.post<boolean>(url, email);
  }

  confirmEmail(confirmObj: ConfirmEmail) {
    const url = `${this.companyApiUrl}/Users/ConfirmEmail`;
    return this.http.post<boolean>(url, confirmObj);
  }

  getPermissionsByUserId(userId: number) {
    const url = `${this.companyApiUrl}/Users/${userId}/Roles`;
    return this.http.get<UserPermissions[]>(url);
  }

  setLookups() {
    const url = `${this.companyApiUrl}/StaticLookUps`;
    return this.http.get<StaticLookup[]>(url);
  }

  getLookUpByName(name: string): Observable<any> {
    const enums = JSON.parse(localStorage.getItem('enums') || '[]');
    const option = enums.find((item: any) => item.name === name);
    return of(option ? option.items : []);
  }

  getCompanyApps(companyId: number): Observable<any> {
    if (this.companyAppsCache.has(companyId)) {
      return this.companyAppsCache.get(companyId)!; // return cached Observable
    }

    const url = `${this.companyApiUrl}/Companies/${companyId}/Apps`;
    const request$ = this.http.get(url).pipe(
      shareReplay(1), // cache the response for subscribers
    );

    this.companyAppsCache.set(companyId, request$); // store the Observable in cache
    return request$;
  }

  // Optional: Method to clear cache (e.g., on logout or refresh)
  clearCompanyAppsCache(companyId?: number): void {
    if (companyId !== undefined) {
      this.companyAppsCache.delete(companyId);
    } else {
      this.companyAppsCache.clear();
    }
  }

  // Service for showing user friendly message
  getErrorMessage(err: any): boolean {
    if (!err) return true; // No error, safe to close

    // For validation errors (400), keep dialog open so user can fix them
    if (err.status === 400 && err.error?.errors?.length) {
      // Process all validation errors
      if (err.error.errors.length === 1) {
        // Single error - simple message
        const error = err.error.errors[0];
        const validationMessage = `${error.field}: ${error.message}`;
        this.toastr.clientError(validationMessage, 400);
      } else {
        // Multiple errors - format as list
        const validationMessages = err.error.errors
          .map((error: any) => `• ${error.field}: ${error.message}`)
          .join('\n');

        this.toastr.clientError(`${validationMessages}`, 400);
      }
      return false; // Don't close - user needs to fix validation errors
    }
    // For unauthorized/forbidden, redirect happens in interceptor
    else if (err.status === 401 || err.status === 403) {
      // No need for toast since interceptor handles these
      return true; // Close dialog as user will be redirected
    }
    // For server errors, display message
    else if (err.status === 500) {
      this.toastr.serverError(
        'Server error (500): An unexpected error occurred. Please check logs or contact the administrator.',
        500,
      );
      return true; // Close dialog as nothing can be fixed here
    }
    // For all other errors
    else {
      // Use clientError or serverError based on status code
      if (err.status >= 400 && err.status < 500) {
        this.toastr.clientError(
          err.error?.detail || 'Oops! Something went wrong. Please try again.',
          err.status,
        );
      } else {
        this.toastr.serverError(
          err.error?.detail || 'A server error occurred. Please try again later.',
          err.status || 500,
        );
      }
      return true; // Close dialog by default for other errors
    }
  }

  getCountries() {
    const url = `${this.companyApiUrl}/Countries`;
    return this.http.get(url);
  }

  getStates(countryId: number) {
    const url = `${this.companyApiUrl}/States?CountryId=${countryId}`;
    return this.http.get(url);
  }

  getTimezones() {
    const url = `${this.companyApiUrl}/TimeZones`;
    return this.http.get(url);
  }

  // Sibling or Unrelated Components Implementation (Service for getting userpermission from any component)
  private callMethodSource = new Subject<void>();
  callMethod$ = this.callMethodSource.asObservable();

  triggerMethodCall() {
    this.callMethodSource.next();
  }
  getDatadogConfig(companyId: number, isupdate: boolean): Observable<RumSettings> {
    if (this.rumSettings$ && !isupdate) {
      return this.rumSettings$;
    }
    const url = `${this.companyApiUrl}/GlobalSettings?CompanyId=${companyId}`;
    this.rumSettings$ = this.http.get<RumSettings>(url).pipe(
      shareReplay(1),
      catchError((err) => {
        // Clear cache on error
        this.rumSettings$ = null;
        throw err;
      }),
    );

    return this.rumSettings$;
  }
}
